import { backgroundOrange } from '@/theme/colors'
import { AttachmentSection } from '@components/Attachments'
import { PlaceSuggestion } from '@components/LocationAutocomplete'
import Modal from '@components/Modal'
import { EventCategory } from '@orbit/api-types'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import IdeaForm from '../AddIdeaModal/IdeaForm'
import { ButtonGroup, CancelButton, SubmitButton } from '../AddIdeaModal/IdeaForm.styles'
import { Idea } from '../types'

interface EditIdeaModalProps {
	isOpen: boolean
	onClose: () => void
	onSubmit: (ideaData: {
		name: string
		description?: string
		category?: EventCategory
		location?: {
			fullText: string
			primaryText: string
			secondaryText?: string
			googlePlaceId?: string
			googleTypes: string[]
		}
		links?: string[]
	}) => void
	onDelete?: () => void
	idea: Idea | null
}

const EditIdeaModal: React.FC<EditIdeaModalProps> = ({ isOpen, onClose, onSubmit, onDelete, idea }) => {
	const { tripId } = useParams<{ tripId: string }>()
	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [category, setCategory] = useState<EventCategory | undefined>(undefined)
	const [locationPrimaryText, setLocationPrimaryText] = useState('')
	const [selectedPlace, setSelectedPlace] = useState<PlaceSuggestion | null>(null)
	const [links, setLinks] = useState<string[]>([])

	// Populate form when idea changes or modal opens
	useEffect(() => {
		if (isOpen && idea) {
			setName(idea.name || '')
			setDescription(idea.description || '')
			setCategory(idea.category || undefined)
			setLocationPrimaryText(idea.location?.primaryText || '')
			// If we have full location data, populate selectedPlace
			if (idea.location?.googlePlaceId) {
				setSelectedPlace({
					placeId: idea.location.googlePlaceId,
					primaryText: idea.location.primaryText || '',
					secondaryText: idea.location.secondaryText || '',
					fullText: idea.location.fullText,
					types: idea.location.googleTypes || [],
				})
			} else {
				setSelectedPlace(null)
			}
		// Convert links to simple URL array for the form
		setLinks(idea.links?.map(link => link.url) || [])
		}
	}, [isOpen, idea])

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (name.trim()) {
			// Filter out empty links
			const filteredLinks = links.filter(link => link.trim() !== '')

			onSubmit({
				name: name.trim(),
				description: description.trim() || undefined,
				category: category || undefined,
				location: selectedPlace ? {
					fullText: selectedPlace.fullText,
					primaryText: selectedPlace.primaryText,
					secondaryText: selectedPlace.secondaryText || undefined,
					googlePlaceId: selectedPlace.placeId,
					googleTypes: selectedPlace.types,
				} : locationPrimaryText.trim() ? {
					fullText: locationPrimaryText.trim(),
					primaryText: locationPrimaryText.trim(),
					googleTypes: [],
				} : undefined,
				links: filteredLinks.length > 0 ? filteredLinks : undefined,
			})
			onClose()
		}
	}

	const handleLocationSelect = (place: PlaceSuggestion) => {
		setSelectedPlace(place)

		// When location is set, attempt to prefill name as well
		// Get the old location name from selectedPlace or locationPrimaryText
		const oldLocationName = (selectedPlace?.primaryText ?? selectedPlace?.fullText ?? locationPrimaryText ?? '')
			.substring(0, 75)
			.trim()

		// If name is empty or matches the old location name, update it with the new location
		const trimmedName = name.trim()
		if (trimmedName === '' || trimmedName === oldLocationName) {
			// Max length on name is 75 (same as iOS)
			const newName = (place.primaryText ?? place.fullText)
				.substring(0, 75)
				.trim()
			setName(newName)
		}
	}

	const handleCancel = () => {
		onClose()
	}

	const handleDelete = () => {
		if (onDelete) {
			onDelete()
		}
	}

	const formId = 'edit-idea-form'

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleCancel}
			title="Edit Idea"
			closeOnBackdropClick={false}
			backgroundColor={backgroundOrange}
			onDelete={onDelete ? handleDelete : undefined}
			footer={
				<ButtonGroup>
					<CancelButton type="button" onClick={handleCancel}>
						Cancel
					</CancelButton>
					<SubmitButton type="submit" form={formId} disabled={!name.trim()}>
						Save Changes
					</SubmitButton>
				</ButtonGroup>
			}
		>
		<IdeaForm
			formId={formId}
			name={name}
			description={description}
			category={category}
			locationPrimaryText={locationPrimaryText}
			locationSecondaryText={selectedPlace?.secondaryText || undefined}
			links={links}
				onNameChange={setName}
				onDescriptionChange={setDescription}
				onCategoryChange={setCategory}
				onLocationPrimaryTextChange={setLocationPrimaryText}
				onLocationSelect={handleLocationSelect}
				onLinksChange={setLinks}
				onSubmit={handleSubmit}
				attachmentSlot={
					tripId && idea ? (
						<AttachmentSection
							tripId={tripId}
							resourceType="IDEA"
							resourceId={idea.id}
							fileIds={idea.fileIds || []}
						/>
					) : undefined
				}
			/>
		</Modal>
	)
}

export default EditIdeaModal
